import { InputWrapperProps } from '@mantine/core';
import React from 'react';
import { useChangeTypes } from 'src/api/queries/changeTypes/useChangeTypes';
import { useCreateChangeType } from 'src/api/queries/changeTypes/useChangeTypeMutations';
import { CreatableSelect } from 'src/design/organisms/CreatableSelect/CreatableSelect';

export interface IChangeTypeValue {
  id: string;
  name: string;
}

export interface IChangeTypeSelectProps
  extends Omit<InputWrapperProps, 'onChange'> {
  value: IChangeTypeValue | null;
  onChange: (value: IChangeTypeValue | null) => void;
  disabled?: boolean;
}

export const ChangeTypeSelect: React.FC<IChangeTypeSelectProps> = ({
  value,
  onChange,
  label = 'Change Type',
  placeholder = 'Select or create change type...',
  disabled = false,
  ...props
}) => {
  const { data } = useChangeTypes();
  const createType = useCreateChangeType();

  const types = React.useMemo(
    () =>
      (data ?? [])
        .filter((t) => t.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [data],
  );

  const createNewType = async (
    name: string,
  ): Promise<IChangeTypeValue | null> => {
    const nextSortOrder =
      (data ?? []).reduce((max, t) => Math.max(max, t.sortOrder), 0) + 1;
    // CreatableSelect awaits onCreate without a catch, so a rejection here
    // would surface as an unhandled rejection.
    const created = await createType
      .mutateAsync({ name, isActive: true, sortOrder: nextSortOrder })
      .catch(() => null);
    return created ? { id: created.id, name: created.name } : null;
  };

  const items = types.map((t) => ({ id: t.id, name: t.name }));

  return (
    <CreatableSelect
      items={items}
      value={value}
      onChange={onChange}
      onCreate={createNewType}
      emptyMessage="No change types..."
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      clearable
      clearAriaLabel="Clear change type"
      {...props}
    />
  );
};
